import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { PLATFORM_ENVIRONMENT, VerificationResult } from '@platform-frontend/platform';
import {
  ClearNotifications,
  InvalidStateError,
  PlannedError,
  ShowErrorNotification,
  ShowInfoNotification,
  SimpleRouterState,
  handleErrors,
  tapNextOrError,
  throwAndHandleError
} from '@platform-frontend/util';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take, tap, timeout } from 'rxjs/operators';
import { ACCOUNT_ERROR_HANDLERS } from '../account-error-handlers';
import { AccountRouteService } from '../account-router';
import { SetAuthenticationContext } from '../auth-context';
import { SetAuthFormEmail } from '../auth-form';
import { LinkAccountState } from '../link-account';
import { LINK_ACCOUNT_ERROR_HANDLERS } from '../link-account/link-account-error-handlers';
import {
  MSG_AUTHENTICATE_EMAIL_DEFAULT,
  MSG_AUTHENTICATE_EMAIL_MAX_MAIL_REQUESTS_EXCEEDED,
  MSG_EMAIL_AUTHENTICATION_SIGNED_IN_WITH_ANOTHER_ACCOUNT,
  MSG_RESEND_AUTHENTICATION_EMAIL_DEFAULT,
  MSG_RESEND_AUTHENTICATION_EMAIL_MAX_MAIL_REQUESTS_EXCEEDED,
  MSG_RESEND_AUTHENTICATION_EMAIL_SERVER_UNREACHABLE,
  MSG_RESEND_AUTHENTICATION_EMAIL_SUCCESS,
  MSG_VERIFY_EMAIL_AUTHENTICATION_DEFAULT
} from '../notification-messages';
import { GotoSignIn, Session, SessionState, SetSession, SignOut, SignOutOnError, UpdateSessionFromBrowser } from '../session';
import {
  AuthenticateEmail,
  CancelEmailAuthentication,
  CompleteEmailAuthentication,
  ResendAuthenticationEmail,
  VerifyEmailAuthentication
} from './email-authentication.actions';
import { AuthenticateEmailResponse } from './model/authenticate-email-response';
import { EmailAuthenticationStateModel } from './model/email-authentication-state-model';

const DEFAULT_STATE: EmailAuthenticationStateModel = {
  email: null,
  passwordSet: null,
  verificationRef: null,
  verified: false
};

@State<EmailAuthenticationStateModel>({
  name: 'emailAuthentication',
  defaults: DEFAULT_STATE
})
@Injectable()
export class EmailAuthenticationState {
  constructor(private accountRouteService: AccountRouteService, private http: HttpClient, private store: Store) {}

  private static readonly POLLING_TAG = Symbol('EmailAuthenticationState: PollingTag');
  private static readonly RESEND_EMAIL_TAG = Symbol('EmailAuthenticationState: ResendEmailTag');

  email$: Observable<string | null> = this.store.select(SessionState.email);
  linkedAccountId$: Observable<string | null> = this.store.select(LinkAccountState.accountId);

  @Selector([SessionState.email])
  static email(state: EmailAuthenticationStateModel, sessionEmail: string | null): string | null {
    return state.email ?? sessionEmail;
  }

  @Selector()
  static verified(state: EmailAuthenticationStateModel): boolean {
    return state.verified;
  }

  @Selector()
  static verificationOngoing(state: EmailAuthenticationStateModel): boolean {
    return state.verificationRef !== null;
  }

  @Selector()
  static passwordRequired(state: EmailAuthenticationStateModel): boolean {
    return state.passwordSet === true;
  }

  @Action(GotoSignIn)
  gotoSignIn(ctx: StateContext<EmailAuthenticationStateModel>, { extras }: GotoSignIn): void {
    if (extras?.signInMode === 'AUTO') ctx.patchState(DEFAULT_STATE);
  }

  @Action(SignOut)
  signOut(ctx: StateContext<EmailAuthenticationStateModel>): void {
    ctx.patchState(DEFAULT_STATE);
  }

  @Action(AuthenticateEmail)
  authenticateEmail(ctx: StateContext<EmailAuthenticationStateModel>, { email, overwriteRedirectUrl, isAutoInit }: AuthenticateEmail): Observable<unknown> {
    return ctx.dispatch(new SignOut('none')).pipe(
      switchMap(() => ctx.dispatch(new SetAuthFormEmail(email))),
      switchMap(() => this.linkedAccountId$),
      take(1),
      switchMap(linkedAccountId =>
        this.http.post<AuthenticateEmailResponse>(PLATFORM_ENVIRONMENT.apiUrl + '/auth/init', {
          email,
          redirect: overwriteRedirectUrl ?? this.accountRouteService.getRedirectUrl(this.store.selectSnapshot(SimpleRouterState.paramMap)),
          linkedAccountId,
          isAutoInit
        })
      ),
      tap(response =>
        ctx.patchState({
          email: response.authenticatingEmail,
          passwordSet: response.passwordSet,
          verificationRef: response.verificationRef,
          verified: false
        })
      ),
      switchMap(response =>
        ctx.dispatch([new SetAuthFormEmail(response.authenticatingEmail), new SetAuthenticationContext(response.userExists ? 'SIGN_IN' : 'SIGN_UP')])
      ),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        ...LINK_ACCOUNT_ERROR_HANDLERS(this.store),
        MAX_MAIL_REQUESTS_EXCEEDED: new ShowErrorNotification(MSG_AUTHENTICATE_EMAIL_MAX_MAIL_REQUESTS_EXCEEDED),
        DEFAULT: new ShowErrorNotification(MSG_AUTHENTICATE_EMAIL_DEFAULT)
      })
    );
  }

  @Action(VerifyEmailAuthentication)
  verifyEmailAuthentication(ctx: StateContext<EmailAuthenticationStateModel>, { timeoutInMilliseconds }: VerifyEmailAuthentication): Observable<unknown> {
    const params = this.getVerificationRef(ctx);
    const nOptions = { keepExisting: true, tags: [EmailAuthenticationState.POLLING_TAG] };
    return this.http.get<VerificationResult>(PLATFORM_ENVIRONMENT.apiUrl + '/account/verified', { params }).pipe(
      timeout({ each: timeoutInMilliseconds - 50, with: () => throwError(() => new PlannedError('SERVER_UNREACHABLE')) }),
      catchError(error => this.updateAndCheckBrowserSession(ctx, error, params.verificationRef)),
      tap(({ verified }) => ctx.patchState({ verified })),
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(EmailAuthenticationState.POLLING_TAG))),
      handleErrors({
        DEFAULT: new ShowErrorNotification(MSG_VERIFY_EMAIL_AUTHENTICATION_DEFAULT, nOptions),
        NOT_FOUND: new GotoSignIn({ signInMode: 'ON_ERROR' })
      })
    );
  }

  private updateAndCheckBrowserSession(
    ctx: StateContext<EmailAuthenticationStateModel>,
    error: any,
    verificationRef: string
  ): Observable<VerificationResult | never> {
    return ctx.dispatch(new UpdateSessionFromBrowser()).pipe(
      switchMap(() => this.isAlreadyAuthenticated(ctx)),
      switchMap(authenticated => (authenticated ? of({ verificationRef, verified: true }) : throwError(() => error)))
    );
  }

  @Action(ResendAuthenticationEmail)
  resendAuthenticationEmail(ctx: StateContext<EmailAuthenticationStateModel>): Observable<void> {
    const nOptions = { keepExisting: true, tags: [EmailAuthenticationState.RESEND_EMAIL_TAG] };
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/account/resendVerificationMail', this.getVerificationRef(ctx)).pipe(
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(EmailAuthenticationState.RESEND_EMAIL_TAG))),
      switchMap(() => ctx.dispatch(new ShowInfoNotification(MSG_RESEND_AUTHENTICATION_EMAIL_SUCCESS, nOptions))),
      handleErrors(ACCOUNT_ERROR_HANDLERS, {
        MAX_MAIL_REQUESTS_EXCEEDED: new ShowErrorNotification(MSG_RESEND_AUTHENTICATION_EMAIL_MAX_MAIL_REQUESTS_EXCEEDED, nOptions),
        SERVER_UNREACHABLE: new ShowErrorNotification(MSG_RESEND_AUTHENTICATION_EMAIL_SERVER_UNREACHABLE, nOptions),
        DEFAULT: new ShowErrorNotification(MSG_RESEND_AUTHENTICATION_EMAIL_DEFAULT, nOptions)
      })
    );
  }

  @Action(CompleteEmailAuthentication)
  completeEmailAuthentication(ctx: StateContext<EmailAuthenticationStateModel>): Observable<unknown> {
    return this.isAuthenticationCompleted(ctx).pipe(
      switchMap(isAuthenticationCompleted => (isAuthenticationCompleted ? of(null) : this.login(ctx))),
      tap(() => ctx.patchState(DEFAULT_STATE))
    );
  }

  private isAuthenticationCompleted(ctx: StateContext<EmailAuthenticationStateModel>): Observable<boolean> {
    const state = ctx.getState();
    return state.verificationRef === null || state.email === null ? of(true) : this.isAlreadyAuthenticated(ctx);
  }

  private isAlreadyAuthenticated(ctx: StateContext<EmailAuthenticationStateModel>): Observable<boolean> {
    const stateEmail = ctx.getState().email;

    if (stateEmail === null) {
      throw new InvalidStateError('Email authentication is not initialized.');
    }

    return this.email$.pipe(
      take(1),
      switchMap(sessionEmail =>
        sessionEmail !== null && sessionEmail !== stateEmail
          ? throwAndHandleError(
              new SignOutOnError(new ShowErrorNotification(MSG_EMAIL_AUTHENTICATION_SIGNED_IN_WITH_ANOTHER_ACCOUNT)),
              new PlannedError('SIGNED_IN_WITH_ANOTHER_ACCOUNT')
            )
          : of(sessionEmail !== null)
      )
    );
  }

  private login(ctx: StateContext<EmailAuthenticationStateModel>): Observable<void> {
    return this.http
      .post<Session>(PLATFORM_ENVIRONMENT.apiUrl + '/account/login', this.getVerificationRef(ctx))
      .pipe(switchMap(session => ctx.dispatch(new SetSession(session))));
  }

  @Action(CancelEmailAuthentication)
  cancelSignUp(ctx: StateContext<EmailAuthenticationStateModel>): Observable<unknown> {
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/account/cancelVerification', this.getVerificationRef(ctx)).pipe(
      catchError(() => of(null)),
      tap(() => ctx.patchState(DEFAULT_STATE))
    );
  }

  private getVerificationRef(ctx: StateContext<EmailAuthenticationStateModel>): { verificationRef: string } {
    const { verificationRef } = ctx.getState();

    if (verificationRef === null) {
      throw new InvalidStateError('Email authentication is not initialized.');
    }

    return { verificationRef };
  }
}
